import { Link } from '../i18n/LocalizedLink'
import { useLocale } from '../i18n/LocaleProvider'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useArticleTelemetry } from '../hooks/useArticleTelemetry'
import Navbar from '../components/Navbar'
import Footer from '../components/Footer'
import NotFoundPage from './NotFoundPage'
import BlogPostBody from '../components/BlogPostBody'
import { usePageTitle } from '../hooks/usePageTitle'
import { getPublishedPost, listPublishedPosts } from '../services/blogService'
import { usePreloadedData } from '../PreloadedDataContext'
import { PUBLICATION_LABELS, type Publication, type BlogPost } from '../types/blog'
import './ArticlePage.css'
import './BlogPostPage.css'

function formatDate(value: string | null, locale: 'en' | 'ja') {
  if (!value) return ''
  return new Intl.DateTimeFormat(locale === 'ja' ? 'ja-JP' : 'en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value))
}

type BlogPostState = {
  slug: string
  post: BlogPost | null
  posts: BlogPost[]
  loading: boolean
  failed: boolean
}

export default function BlogPostPage({ publication = 'blog' }: { publication?: Publication }) {
  const { locale, t } = useLocale()
  const publicationLabel = t(PUBLICATION_LABELS[publication])
  const { slug = '' } = useParams<{ slug: string }>()
  const preloadedData = usePreloadedData()
  const preloadedPost = preloadedData && 'slug' in preloadedData && preloadedData.kind === `${publication}-post` && preloadedData.slug === slug
    ? preloadedData
    : null
  const [state, setState] = useState<BlogPostState>(() => ({
    slug,
    post: preloadedPost?.post ?? null,
    posts: preloadedPost?.posts ?? [],
    loading: preloadedPost === null,
    failed: false,
  }))
  const current = state.slug === slug
    ? state
    : { slug, post: null, posts: [], loading: true, failed: false }

  useEffect(() => {
    let active = true
    document.body.classList.add('body-white')

    Promise.all([getPublishedPost(slug, publication), listPublishedPosts(publication)])
      .then(([found, allPosts]) => {
        if (!active) return
        setState({ slug, post: found, posts: allPosts, loading: false, failed: false })
      })
      .catch(() => {
        if (!active) return
        if (preloadedPost) {
          setState({
            slug,
            post: preloadedPost.post,
            posts: preloadedPost.posts,
            loading: false,
            failed: false,
          })
          return
        }
        setState({ slug, post: null, posts: [], loading: false, failed: true })
      })

    return () => {
      active = false
      document.body.classList.remove('body-white')
    }
  }, [preloadedPost, slug, publication])

  useArticleTelemetry(slug, publication, !current.loading && current.post?.slug === slug && current.post.publication === publication)

  usePageTitle(current.post?.title ?? (current.loading ? publicationLabel : t("Story not found")), `Bybeit ${publicationLabel}`, false)

  if (current.loading) {
    return (
      <>
        <Navbar lightMode />
        <main className="article-page blog-post-state container" aria-busy="true">
          <p>{t("Loading story…")}</p>
        </main>
        <Footer />
      </>
    )
  }

  if (!current.post && !current.failed) {
    return <NotFoundPage section={publication} />
  }

  if (current.failed || !current.post) {
    return (
      <>
        <Navbar lightMode />
        <main className="article-notfound container">
          <h1>{current.failed ? t("This story is temporarily unavailable.") : t("Story not found.")}</h1>
          <Link to={`/${publication}`} className="article-back">← {t('Back to {section}', { section: publicationLabel })}</Link>
        </main>
        <Footer />
      </>
    )
  }

  const post = current.post
  const currentIndex = current.posts.findIndex(item => item.slug === post.slug)
  const newerPost = currentIndex > 0 ? current.posts[currentIndex - 1] : null
  const olderPost = currentIndex >= 0 && currentIndex < current.posts.length - 1 ? current.posts[currentIndex + 1] : null

  return (
    <>
      <Navbar lightMode />

      <article className="article-page">
        <div className="container">
          <div className="article-inner">
            <header className="article-header">
              <div className="article-meta">
                <span className="article-category">{post.category.toUpperCase()}</span>
                <span className="article-date">{formatDate(post.publishedAt, locale)}</span>
              </div>
              <h1 className="article-title">{post.title}</h1>
              <p className="article-lead">{post.excerpt}</p>
              <p className="blog-post-author">{t("By")} {post.author}</p>
            </header>
          </div>

          {post.coverImage && (
            <div className="article-cover-wrap">
              <img src={post.coverImage} alt="" className="article-cover" />
              {post.coverImageCredit && (
                <p className="article-cover-credit"> {t("Photo by")}{' '}
                  <a href={post.coverImageCredit.authorUrl} target="_blank" rel="noopener noreferrer">{post.coverImageCredit.author}</a>
                  {' '}{t("on")}{' '}
                  <a href={post.coverImageCredit.sourceUrl} target="_blank" rel="noopener noreferrer">Unsplash</a>
                </p>
              )}
            </div>
          )}

          <div className="article-inner">
            <BlogPostBody content={post.content} />
          </div>

          <div className="divider article-footer-divider" />

          <div className="article-inner">
            <div className="article-pagination">
              <div className="article-pagination-slot">
                {newerPost && (
                  <Link to={`/${publication}/${newerPost.slug}`} className="article-page-link article-page-link--prev">
                    <span className="article-page-label">{t("← Newer story")}</span>
                    <span className="article-page-title">{newerPost.title}</span>
                  </Link>
                )}
              </div>
              <div className="article-pagination-slot article-pagination-slot--right">
                {olderPost && (
                  <Link to={`/${publication}/${olderPost.slug}`} className="article-page-link article-page-link--next">
                    <span className="article-page-label">{t("Older story →")}</span>
                    <span className="article-page-title">{olderPost.title}</span>
                  </Link>
                )}
              </div>
            </div>

            <Link to={`/${publication}`} className="article-back article-back--bottom">{t('Back to {section}', { section: publicationLabel })}</Link>
          </div>
        </div>
      </article>

      <Footer />
    </>
  )
}
